import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, ErrorHandler } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';

import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
  provideMsalAuth,
} from './msal.config';
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MsalService,
  MsalBroadcastService,
  MsalGuard,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
} from '@azure/msal-angular';

import { ToastrModule } from 'ngx-toastr';

// Configs
import { DEVTOOLS_REDUX_CONFIG } from './store/store.config';

// Environments
import { environment } from '@env';

// Interceptors

// Routes
import { routes } from './app.routes';

// Store
import { AuthState } from './store/auth/auth.state';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';

// Plotly
import * as PlotlyJS from 'plotly.js-dist-min';
import { PlotlyModule } from 'angular-plotly.js';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { UIState } from './store/app/states/ui/ui.state';
import { UserState } from './store/app/states/user/user.state';
import { IncidentsState } from './store/app/states/incidents/incidents.state';
import { RimsState } from './store/app/states/rims/rims.state';
import { IncidentAnalyticsState } from './store/app/states/analytics/analytics.state';
import { GlobalHttpInterceptor } from './interceptors/global-http.interceptor';
import { getAppMaterialDateProviders } from './shared/app-material-date-providers';

export const appConfig: ApplicationConfig = {
  providers: [
    // Generic NG providers
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withFetch(), withInterceptors([GlobalHttpInterceptor]), withInterceptorsFromDi()),

    ...getAppMaterialDateProviders(),

    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },

    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),

    // MSAL Auth
    provideMsalAuth,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: ErrorHandler,
      useClass: environment.isProduction ? ApplicationinsightsAngularpluginErrorService : ErrorHandler,
    },
    MsalGuard,
    MsalService,
    MsalBroadcastService,

    // Plotly
    importProvidersFrom(PlotlyModule.forRoot(PlotlyJS)),

    // Store management providers

    // We don't want to provide the NGXS states for the tests. Each test setup should provide only the states that it needs in the TestBed.
    ...(environment.envName === 'Unit Test'
      ? []
      : [
          importProvidersFrom(
            NgxsModule.forRoot([AuthState, UIState, UserState, IncidentsState, RimsState, IncidentAnalyticsState], {
              developmentMode: !environment.isProduction,
            }),
          ),
          importProvidersFrom(
            NgxsStoragePluginModule.forRoot({
              keys: ['authState', 'user'],
              namespace: 'explorer',
            }),
          ),
        ]),

    importProvidersFrom(NgxsReduxDevtoolsPluginModule.forRoot(DEVTOOLS_REDUX_CONFIG)),
    importProvidersFrom(NgxsFormPluginModule.forRoot()),

    // Additional providers
    importProvidersFrom(ToastrModule.forRoot()),
  ],
};
